import type React from 'react';
import { CSS, type Transform } from '@dnd-kit/utilities';

/**
 * Inline style for a drag-to-reorder Table Explorer column header.
 *
 * The caller spreads the result onto the `<th>` AFTER the header's sticky
 * style, so this object must not carry keys it has no opinion about.
 *
 * `zIndex` is therefore emitted ONLY while dragging. Object spread copies keys
 * whose value is `undefined`, so an unconditional `zIndex: isDragging ? 35 :
 * undefined` overwrote the sticky header's z-index (25, or 30 when pinned) with
 * `undefined`, React emitted no z-index, and the header fell back to `z-index:
 * auto`. It then painted in tree order — before `<tbody>` — so row content that
 * is positioned or makes its own stacking context (the `opacity-60` FK popout
 * icons, the `relative` expand-affordance wrapper) scrolled OVER the header
 * instead of under it, while plain cell text still went under.
 */
export function buildSortableHeaderStyle(
  transform: Transform | null,
  transition: string | undefined,
  isDragging: boolean,
): React.CSSProperties {
  return {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...(isDragging ? { zIndex: 35 } : {}),
  };
}
